import { AnalyticsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { canSeeAnalyticsSection } from "@redotech/redo-analytics-common/analytics-section-access";
import { useTranslation } from "@redotech/redo-i18n";
import AnalyticsIcon from "@redotech/redo-icons/bar-chart-01.svg";
import AnalyticsFilledIcon from "@redotech/redo-icons/bar-chart-01_filled.svg";
import ReportIcon from "@redotech/redo-icons/file-02.svg";
import ReportFilledIcon from "@redotech/redo-icons/file-02_filled.svg";
import HomeIcon from "@redotech/redo-icons/home-02.svg";
import HomeFilledIcon from "@redotech/redo-icons/home-02_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";

/** Top-level Analytics; allowlisted users only for now. */
export function useAnalyticsNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const user = useContext(UserContext);
  const canViewAnalytics = useHasPermission(
    AnalyticsPermissions.VIEW_ANALYTICS,
  );

  if (!canSeeAnalyticsSection(user?.email) || !canViewAnalytics) {
    return null;
  }

  const friendlyName = t("Analytics", {
    context: "primary navigation section for merchant reports",
  });
  const primaryTab: LinkPrimaryTab = {
    id: "analytics",
    hasNotification: false,
    UnfilledIcon: AnalyticsIcon,
    FilledIcon: AnalyticsFilledIcon,
    type: "link",
    href: routes.analyticsOverviewHref,
    pathRoot: routes.analyticsBaseUrl,
    friendlyName,
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "analytics",
    name: friendlyName,
    NavContent: SecondaryAnalyticsNavigation,
  };

  return { primaryTab, secondaryNavigationData, isCore: true };
}

const SecondaryAnalyticsNavigation = memo(
  function SecondaryAnalyticsNavigation() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: "Analytics",
    });

    return (
      <TreeContext.Provider value={nodeId}>
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.analyticsOverviewHref }}
          FilledIcon={HomeFilledIcon}
          friendlyName={t("Home", {
            context:
              "Analytics navigation label for the analytics landing page",
          })}
          id="analytics-home"
          UnfilledIcon={HomeIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.analyticsCustomReportsHref }}
          FilledIcon={ReportFilledIcon}
          friendlyName={t("Reports", {
            context:
              "Analytics navigation label for custom and default reports",
          })}
          id="analytics-reports"
          UnfilledIcon={ReportIcon}
        />
      </TreeContext.Provider>
    );
  },
);
